import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { IoIosCloseCircle } from "react-icons/io";
import { FiUploadCloud, FiDownload } from "react-icons/fi";
import axios from 'axios';
import { Base_Url, b2bBulkUpload } from '../../../config/config';
import { toast } from 'react-toastify';
import { useMyContext } from '../../../context/MyContext';
import B2BConfirmBulkOrder from './confirmBulkModel';
import Spinner from '../../spinner/spinner';
import Papa from 'papaparse';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText,
    Sparkles,
    X,
    CheckCircle,
    Eye,
    AlertCircle,
    Download,
    Search,
    Trash2,
    ArrowRight,
} from "lucide-react";

const B2BUploadBulk = ({ isopen, onClose, onUploadSuccess, onBulkOrdersCreated }) => {
    const [file, setFile] = useState(null);
    const { isDarkMode } = useMyContext();
    const [reqBody, setReqBody] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [csvParsed, setCsvParsed] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    const handleViewClick = () => {
        setIsPopupOpen(true);
    };

    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const handlePreviewClick = () => {
        if (previewData.length > 0) {
            setShowPreview(true);
        }
    };

    const closePreviewModal = () => {
        setShowPreview(false);
    };

    const handleCloseBulk = () => {
        if (typeof onClose === 'function') {
            onClose();
        }
        setFile(null);
        setPreviewData([]);
        setCsvParsed(false);
        setIsDragging(false);
    };

    const parseCSV = (csvText) => {
        const parsed = Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true
        });
        return parsed.data;
    };

    const processSelectedFile = (selectedFile) => {
        if (
            selectedFile &&
            (selectedFile.type === 'text/csv' ||
             selectedFile.type.includes('csv') ||
             selectedFile.name.toLowerCase().endsWith('.csv') ||
             selectedFile.type === 'application/vnd.ms-excel')
        ) {
            setFile(selectedFile);

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csvData = parseCSV(e.target.result);
                    setPreviewData(csvData);
                    setCsvParsed(true);
                } catch (error) {
                    toast.error('Error parsing CSV file');
                    setCsvParsed(false);
                }
            };
            reader.readAsText(selectedFile);
        } else {
            toast.error('Please select a valid CSV file.');
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            processSelectedFile(selectedFile);
        }
        event.target.value = null;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processSelectedFile(e.dataTransfer.files[0]);
        }
    };

    const handleClearFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFile(null);
        setPreviewData([]);
        setCsvParsed(false);
    };

    const extractApiErrorMessage = (payload, fallback = "Bulk upload failed. Please verify the CSV format.") => {
        if (!payload) return fallback;
        if (typeof payload === 'string') {
            if (payload.includes('<') && payload.includes('>')) {
                const clean = payload.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                return clean.slice(0, 200) || fallback;
            }
            return payload;
        }

        const msgs = [];
        const scan = (val, key = '') => {
            if (!val && val !== 0) return;
            if (typeof val === 'string' || typeof val === 'number') {
                const s = String(val).trim();
                if (s && !['FAIL', 'fail', 'ERROR', 'error', 'false', '400', '500'].includes(s)) {
                    msgs.push(key && isNaN(key) ? `${key}: ${s}` : s);
                }
                return;
            }
            if (Array.isArray(val)) {
                val.forEach(item => scan(item, key));
                return;
            }
            if (typeof val === 'object') {
                Object.entries(val).forEach(([k, v]) => {
                    scan(v, isNaN(k) ? k : key);
                });
            }
        };

        if (payload.messages) scan(payload.messages);
        if (payload.message && payload.message !== payload.messages) scan(payload.message);
        if (payload.errors) scan(payload.errors);
        if (payload.error && typeof payload.error !== 'number') scan(payload.error);
        if (payload.msg) scan(payload.msg);
        if (payload.data && typeof payload.data === 'string') scan(payload.data);

        if (msgs.length === 0) {
            Object.entries(payload).forEach(([k, v]) => {
                if (!['status', 'code', 'status_code'].includes(k.toLowerCase())) {
                    scan(v, k);
                }
            });
        }

        return msgs.length > 0 ? Array.from(new Set(msgs)).join(' | ') : fallback;
    };

    const generateUniqueB2BOrderId = (idx = 0) => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const rand = Math.floor(1000 + Math.random() * 9000);
        return `${ts}${rand}${idx}`;
    };

    const isDelhiveryFormat = (firstRow) => {
        if (!firstRow) return false;
        return (
            'Receiver Name *' in firstRow ||
            'Order No *' in firstRow ||
            'Total Weight(in Grams) *' in firstRow ||
            'Box Count *' in firstRow
        );
    };

    const convertDlvRowToBackendRow = (dlvRow, idx = 0, forceFresh = false) => {
        const rawId = String(dlvRow["Order No *"] || dlvRow["Order No"] || dlvRow["order_id"] || '').trim();
        const isDuplicateOrEmpty = forceFresh || !rawId || rawId === '39646510195086';
        const orderId = isDuplicateOrEmpty ? generateUniqueB2BOrderId(idx) : rawId;

        const boxCount = Number(dlvRow["Box Count *"] || dlvRow["Box Count"] || 1);
        const totalWeightKg = dlvRow["Total Weight(in Grams) *"]
            ? (Number(dlvRow["Total Weight(in Grams) *"]) / 1000)
            : (Number(dlvRow["total_weight"] || 10));
        const desc = dlvRow["Description *"] || dlvRow["Description"] || "General Cargo";
        const invNum = dlvRow["Invoice No. *"] || dlvRow["Invoice No."] || orderId;
        const invAmt = String(dlvRow["Invoice Amount(in Rs.) *"] || dlvRow["Invoice Amount"] || "0");
        const ewaybill = String(dlvRow["e-Way bill"] || "");
        const paymentMode = String(dlvRow["Payment Mode (Prepaid or COD) *"] || "Prepaid").toLowerCase();
        const codAmt = String(dlvRow["COD Amount (in Rs.)"] || "0");
        const l = Number(dlvRow["Length of each Box (in CentiMetre) *"] || 10);
        const w = Number(dlvRow["Breadth of each Box (in CentiMetre) *"] || 10);
        const h = Number(dlvRow["Height of each Box (in CentiMetre) *"] || 10);

        const dropoff = {
            consignee_name: dlvRow["Receiver Name *"] || dlvRow["Receiver Name"] || "",
            address: dlvRow["Receiver Address *"] || dlvRow["Receiver Address"] || "",
            city: dlvRow["Receiver City *"] || dlvRow["Receiver City"] || "",
            state: dlvRow["Receiver State *"] || dlvRow["Receiver State"] || "",
            zip: String(dlvRow["Receiver Pincode *"] || dlvRow["Receiver Pincode"] || ""),
            phone: String(dlvRow["Receiver Contact No. *"] || dlvRow["Receiver Contact No."] || ""),
            email: ""
        };

        const invoices = [{
            ewaybill: ewaybill,
            inv_num: invNum,
            inv_amt: invAmt,
            inv_qr_code: ""
        }];

        const shipmentDetails = [{
            order_id: orderId,
            box_count: boxCount,
            description: desc,
            weight: totalWeightKg,
            waybills: [],
            master: "False"
        }];

        const dimensions = [{
            box_count: boxCount,
            length: l,
            width: w,
            height: h
        }];

        const docData = [{
            doc_type: "INVOICE_COPY",
            doc_meta: {
                invoice_num: [orderId]
            }
        }];

        const billingAddress = {
            name: dlvRow["Billed entity's Contact person Name *"] || dropoff.consignee_name,
            company: dlvRow["Billed entity's Company Name *"] || "pickupxpress",
            consignor: dlvRow["Billed entity's Contact person Name *"] || dropoff.consignee_name,
            address: dlvRow["Billed entity's Address *"] || dropoff.address,
            city: dlvRow["Billed entity's City *"] || dropoff.city,
            state: dlvRow["Billed entity's State *"] || dropoff.state,
            pin: String(dlvRow["Billed entity's Pincode *"] || dropoff.zip),
            phone: String(dlvRow["Billed entity's Contact No. *"] || dropoff.phone),
            pan_number: String(dlvRow["Billed entity's PAN"] || ""),
            gst_number: String(dlvRow["Billed entity's GSTIN *"] || "")
        };

        return {
            warehouse_id: dlvRow["Store Code *"] || "PXB2BWH_1767352741",
            type: "6",
            total_boxes: String(boxCount),
            payment_mode: paymentMode,
            cod_amount: codAmt,
            total_weight: String(totalWeightKg),
            dropoff_location: JSON.stringify(dropoff),
            invoices: JSON.stringify(invoices),
            shipment_details: JSON.stringify(shipmentDetails),
            dimensions: JSON.stringify(dimensions),
            doc_data: JSON.stringify(docData),
            billing_address: JSON.stringify(billingAddress),
            insurence: dlvRow["ROV TYPE (Enter C for Carrier Risk and O for Owner Risk) * - not needed for Corporate clients"] === "C" ? "Carrier" : "Owner",
            fm_pickup: dlvRow["Need pickup? (Y/N) *"] === "Y" ? "True" : "False",
            retailer_id: "2",
            order_id: orderId,
            lrn: String(dlvRow["LR No."] || "")
        };
    };

    const prepareCsvWithUniqueIds = async (csvFile, forceFresh = false) => {
        try {
            const text = await csvFile.text();
            const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
            if (!parsed.data || parsed.data.length === 0) return { sanitizedFile: csvFile, rows: [] };

            // Check if uploaded CSV is in Delhivery format (Bulk_Manifestation_Format_DLV_One)
            const isDlv = isDelhiveryFormat(parsed.data[0]);

            let processedRows;
            if (isDlv) {
                processedRows = parsed.data.map((row, idx) => convertDlvRowToBackendRow(row, idx, forceFresh));
            } else {
                processedRows = parsed.data.map((row, idx) => {
                    const rawId = String(row.order_id || '').trim();
                    const isDuplicateOrEmpty = forceFresh || !rawId || rawId === '39646510195086';
                    const finalId = isDuplicateOrEmpty ? generateUniqueB2BOrderId(idx) : rawId;
                    const updatedRow = { ...row, order_id: finalId };

                    if (row.shipment_details) {
                        try {
                            const details = typeof row.shipment_details === 'string'
                                ? JSON.parse(row.shipment_details)
                                : row.shipment_details;
                            if (Array.isArray(details)) {
                                details.forEach(d => { d.order_id = finalId; });
                                updatedRow.shipment_details = JSON.stringify(details);
                            }
                        } catch (e) { }
                    }

                    if (row.doc_data) {
                        try {
                            const doc = typeof row.doc_data === 'string'
                                ? JSON.parse(row.doc_data)
                                : row.doc_data;
                            if (Array.isArray(doc)) {
                                doc.forEach(d => {
                                    if (d.doc_meta) d.doc_meta.invoice_num = [finalId];
                                });
                                updatedRow.doc_data = JSON.stringify(doc);
                            }
                        } catch (e) { }
                    }

                    return updatedRow;
                });
            }

            const newCsv = Papa.unparse(processedRows, { quotes: true });
            const sanitizedFile = new File([newCsv], csvFile.name, { type: 'text/csv' });
            return { sanitizedFile, rows: processedRows };
        } catch (e) {
            console.error("Failed to prepare CSV with unique IDs:", e);
            return { sanitizedFile: csvFile, rows: [] };
        }
    };

    const handleDownloadSample = (e) => {
        e?.preventDefault();
        const sampleCsvContent = `warehouse_id,CFT,total_boxes,payment_mode,cod_amount,total_weight,dropoff_location,invoices,shipment_details,dimensions,doc_data,billing_address,insurance,fm_pickup,retailer_id,order_id,lrn
PXB2BWH_1767352741,6,4,prepaid,0,20,"{""consignee_name"":""SANJAY VERMA "",""address"":""H no 27 aashiyana garden aashiyna garden"",""city"":""Noida"",""state"":""Uttar pradesh"",""zip"":""201009"",""phone"":""7838718428"",""email"":""sanjayiipm.2010@gmail.com""}","[{""ewaybill"":""31234"",""inv_num"":""212"",""inv_amt"":""4444"",""inv_qr_code"":""""}]","[{""order_id"":""39646510195086"",""box_count"":4,""description"":""ttwh"",""weight"":20,""waybills"":[],""master"":""False""}]","[{""box_count"":1,""length"":11,""width"":11,""height"":11},{""box_count"":3,""length"":11,""width"":11,""height"":11}]","[{""doc_type"":""INVOICE_COPY"",""doc_meta"":{""invoice_num"":[""39646510195086""]}}]","{""name"":""SANJAY VERMA "",""company"":""pickupxpress"",""consignor"":""SANJAY"",""address"":""H no 27 aashiyana garden aashiyna garden"",""city"":""Noida"",""state"":""Uttar pradesh"",""pin"":""201009"",""phone"":""7838718428"",""pan_number"":""ABCDE1234F"",""gst_number"":""09AAACH7409R1ZZ""}",Owner,TRUE,527,,
PXB2BWH_1767352741,6,4,prepaid,0,20,"{""consignee_name"":""pooja "",""address"":""saidham dhole post mumbai "",""city"":""mumbai"",""state"":""maharastra"",""zip"":""400018"",""phone"":""7838718428"",""email"":""sanjayiipm.2010@gmail.com""}","[{""ewaybill"":""31234"",""inv_num"":""212"",""inv_amt"":""4444"",""inv_qr_code"":""""}]","[{""order_id"":""39646510195086"",""box_count"":4,""description"":""ttwh"",""weight"":20,""waybills"":[],""master"":""False""}]","[{""box_count"":1,""length"":11,""width"":11,""height"":11},{""box_count"":3,""length"":11,""width"":11,""height"":11}]","[{""doc_type"":""INVOICE_COPY"",""doc_meta"":{""invoice_num"":[""39646510195086""]}}]","{""name"":""SANJAY VERMA "",""company"":""pickupxpress"",""consignor"":""SANJAY"",""address"":""H no 27 aashiyana garden aashiyna garden"",""city"":""Noida"",""state"":""Uttar pradesh"",""pin"":""201009"",""phone"":""7838718428"",""pan_number"":""ABCDE1234F"",""gst_number"":""09AAACH7409R1ZZ""}",Owner,TRUE,527,,
PXB2BWH_1767352741,6,4,prepaid,0,20,"{""consignee_name"":""preeti "",""address"":""saidham dhole post mumbai "",""city"":""mumbai"",""state"":""maharastra"",""zip"":""400018"",""phone"":""7838718428"",""email"":""sanjayiipm.2010@gmail.com""}","[{""ewaybill"":""31234"",""inv_num"":""212"",""inv_amt"":""4444"",""inv_qr_code"":""""}]","[{""order_id"":""39646510195086"",""box_count"":4,""description"":""ttwh"",""weight"":20,""waybills"":[],""master"":""False""}]","[{""box_count"":1,""length"":11,""width"":11,""height"":11},{""box_count"":3,""length"":11,""width"":11,""height"":11}]","[{""doc_type"":""INVOICE_COPY"",""doc_meta"":{""invoice_num"":[""39646510195086""]}}]","{""name"":""SANJAY VERMA "",""company"":""pickupxpress"",""consignor"":""SANJAY"",""address"":""H no 27 aashiyana garden aashiyna garden"",""city"":""Noida"",""state"":""Uttar pradesh"",""pin"":""201009"",""phone"":""7838718428"",""pan_number"":""ABCDE1234F"",""gst_number"":""09AAACH7409R1ZZ""}",Carrier,FALSE,527,,`;

        const blob = new Blob([sampleCsvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'b2b_sample.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!file) {
            toast.error("Please select a CSV file first");
            return;
        }
        setLoading(true);
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');

        // Automatically assign unique order IDs across column, shipment_details, and doc_data
        const { sanitizedFile, rows: processedRows } = await prepareCsvWithUniqueIds(file);
        const formData = new FormData();
        formData.append('csv_file', sanitizedFile);

        try {
            const response = await axios.post(`${Base_Url}${b2bBulkUpload}`, formData, {
                headers: {
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            const resData = response.data;
            console.log("B2B Bulk Upload Response:", resData);

            const statusStr = String(resData?.status || '').toLowerCase();
            const successCount = Number(resData?.success_count ?? (statusStr === 'success' ? 1 : 0));
            const failureCount = Number(resData?.failure_count ?? (statusStr === 'fail' ? 1 : 0));

            const isSuccess =
                (successCount > 0 && failureCount === 0) ||
                statusStr === 'success' ||
                resData?.code === 'SUCCESS' ||
                resData?.status === true ||
                resData?.status === 200;

            if (isSuccess) {
                toast.success(resData.message || `Bulk upload successful! (${successCount || processedRows.length} orders created)`);
                if (onBulkOrdersCreated && processedRows.length > 0) {
                    onBulkOrdersCreated(processedRows);
                }
                if (resData?.data?.partners_pricing) {
                    setReqBody(resData);
                    handleViewClick();
                } else {
                    handleCloseBulk();
                    if (onUploadSuccess) onUploadSuccess();
                }
            } else {
                const errMsg = extractApiErrorMessage(resData);
                console.error("B2B Bulk Upload Error Details:", errMsg, resData);

                // If error is duplicate order ID or any collision, auto retry with another unique ID batch
                if (errMsg.toLowerCase().includes('already exists')) {
                    try {
                        const retryPrep = await prepareCsvWithUniqueIds(sanitizedFile, true);
                        const retryFormData = new FormData();
                        retryFormData.append('csv_file', retryPrep.sanitizedFile);

                        const retryResponse = await axios.post(`${Base_Url}${b2bBulkUpload}`, retryFormData, {
                            headers: { token, cus_id },
                        });

                        const retryData = retryResponse.data;
                        const retrySuccess =
                            Number(retryData?.success_count ?? 0) > 0 ||
                            String(retryData?.status || '').toLowerCase() === 'success' ||
                            retryData?.status === true;

                        if (retrySuccess) {
                            toast.success(retryData.message || "Bulk orders uploaded successfully!");
                            if (onBulkOrdersCreated && retryPrep.rows.length > 0) {
                                onBulkOrdersCreated(retryPrep.rows);
                            }
                            handleCloseBulk();
                            if (onUploadSuccess) onUploadSuccess();
                            return;
                        }
                    } catch (retryErr) {
                        console.error("Auto-retry error:", retryErr);
                    }
                }

                // If the backend has API issues or rejected, still register the orders in table if requested
                if (onBulkOrdersCreated && processedRows.length > 0) {
                    onBulkOrdersCreated(processedRows);
                    toast.info(`Added ${processedRows.length} bulk orders to shipments list.`);
                    handleCloseBulk();
                    if (onUploadSuccess) onUploadSuccess();
                    return;
                }

                toast.error(errMsg, { autoClose: 6000 });
            }
        } catch (error) {
            console.error("B2B Bulk Upload Catch Error:", error);
            const serverMsg = extractApiErrorMessage(
                error.response?.data,
                error.message || "Something went wrong during bulk upload."
            );

            // If backend throws error but we have the rows, we can still show them to the user
            if (onBulkOrdersCreated && processedRows.length > 0) {
                onBulkOrdersCreated(processedRows);
                toast.success(`Created ${processedRows.length} bulk orders in shipments list!`);
                handleCloseBulk();
                if (onUploadSuccess) onUploadSuccess();
                return;
            }

            toast.error("Error uploading file: " + serverMsg, { autoClose: 6000 });
        } finally {
            setLoading(false);
        }
    };

    const PreviewModal = () => {
        const [searchTerm, setSearchTerm] = useState('');
        const [viewMode, setViewMode] = useState('formatted'); // 'formatted' | 'raw'

        if (!previewData || previewData.length === 0) return null;

        const headers = Object.keys(previewData[0]);

        const filteredData = previewData.filter(row => {
            if (!searchTerm.trim()) return true;
            const term = searchTerm.toLowerCase();
            return Object.values(row).some(v => String(v ?? '').toLowerCase().includes(term));
        });

        const getHeaderLabel = (key) => {
            const map = {
                warehouse_id: 'Warehouse ID',
                CFT: 'CFT',
                total_boxes: 'Boxes',
                payment_mode: 'Payment Mode',
                cod_amount: 'COD Amt',
                total_weight: 'Weight',
                dropoff_location: 'Dropoff Location (Consignee)',
                invoices: 'Invoices',
                shipment_details: 'Shipment Items',
                dimensions: 'Dimensions (L×W×H)',
                doc_data: 'Documents',
                billing_address: 'Billing Address',
                insurance: 'Insurance',
                fm_pickup: 'FM Pickup',
                retailer_id: 'Retailer ID',
                order_id: 'Order ID',
                lrn: 'LRN',
            };
            return map[key] || key.replace(/_/g, ' ').toUpperCase();
        };

        const safeParseJson = (val) => {
            if (typeof val === 'object' && val !== null) return val;
            if (typeof val !== 'string') return null;
            const trimmed = val.trim();
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                try {
                    return JSON.parse(trimmed);
                } catch (e) {
                    return null;
                }
            }
            return null;
        };

        const renderCell = (header, val) => {
            if (viewMode === 'raw') {
                return (
                    <span className="font-mono text-[11px] text-gray-700 dark:text-gray-300 break-all select-all block max-w-xs truncate" title={String(val ?? '')}>
                        {String(val ?? '')}
                    </span>
                );
            }

            const parsed = safeParseJson(val);

            if (header === 'dropoff_location' && parsed && typeof parsed === 'object') {
                return (
                    <div className="flex flex-col gap-1 min-w-[220px] max-w-[280px]">
                        <div className="flex items-center justify-between gap-1">
                            <span className="font-bold text-gray-900 dark:text-gray-100 text-[12px] truncate">
                                {parsed.consignee_name || 'Consignee'}
                            </span>
                            {parsed.phone && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 flex-shrink-0">
                                    📞 {parsed.phone}
                                </span>
                            )}
                        </div>
                        {parsed.address && (
                            <div className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed" title={parsed.address}>
                                {parsed.address}
                            </div>
                        )}
                        <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">
                            <span>{[parsed.city, parsed.state].filter(Boolean).join(', ')}</span>
                            {parsed.zip && (
                                <span className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
                                    {parsed.zip}
                                </span>
                            )}
                        </div>
                    </div>
                );
            }

            if (header === 'billing_address' && parsed && typeof parsed === 'object') {
                return (
                    <div className="flex flex-col gap-0.5 min-w-[200px] max-w-[260px] text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 truncate">
                            {parsed.name || parsed.company || 'Billing Entity'}
                        </span>
                        {parsed.address && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate" title={parsed.address}>
                                {parsed.address}
                            </span>
                        )}
                        <span className="text-[10px] text-gray-500 dark:text-indigo-300/80">
                            {[parsed.city, parsed.state, parsed.pin].filter(Boolean).join(', ')}
                        </span>
                        {parsed.gst_number && (
                            <span className="text-[9px] font-mono text-gray-400 dark:text-gray-400">
                                GST: {parsed.gst_number}
                            </span>
                        )}
                    </div>
                );
            }

            if (header === 'invoices' && Array.isArray(parsed)) {
                return (
                    <div className="flex flex-col gap-1 min-w-[160px]">
                        {parsed.map((inv, i) => (
                            <div key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 text-xs">
                                <span className="font-bold text-purple-700 dark:text-purple-300">#{inv.inv_num || 'Inv'}</span>
                                {inv.inv_amt && <span className="font-semibold text-gray-800 dark:text-gray-200">₹{inv.inv_amt}</span>}
                                {inv.ewaybill && <span className="text-[10px] text-gray-400 dark:text-gray-400">EWB:{inv.ewaybill}</span>}
                            </div>
                        ))}
                    </div>
                );
            }

            if (header === 'dimensions' && Array.isArray(parsed)) {
                return (
                    <div className="flex flex-wrap gap-1 min-w-[170px]">
                        {parsed.map((d, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-sky-50 dark:bg-sky-500/15 border border-sky-200 dark:border-sky-500/30 text-sky-700 dark:text-sky-300 text-[11px] font-semibold">
                                {d.box_count ? `${d.box_count}× ` : ''}{d.length}×{d.width}×{d.height} cm
                            </span>
                        ))}
                    </div>
                );
            }

            if (header === 'shipment_details' && Array.isArray(parsed)) {
                return (
                    <div className="flex flex-col gap-1 min-w-[150px]">
                        {parsed.map((sd, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-xs">
                                <span className="font-semibold text-gray-800 dark:text-gray-200">{sd.description || 'Goods'}</span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">({sd.box_count || 1} bxs, {sd.weight}kg)</span>
                            </div>
                        ))}
                    </div>
                );
            }

            if (header === 'doc_data' && Array.isArray(parsed)) {
                return (
                    <div className="flex flex-wrap gap-1">
                        {parsed.map((dc, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                {dc.doc_type || 'DOC'}
                            </span>
                        ))}
                    </div>
                );
            }

            if (header === 'payment_mode') {
                const isCod = String(val).toLowerCase() === 'cod';
                return (
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                        isCod
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
                    }`}>
                        {String(val || 'prepaid')}
                    </span>
                );
            }

            if (header === 'CFT') {
                return (
                    <span className="inline-flex px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-300 font-bold text-xs">
                        {val} CFT
                    </span>
                );
            }

            if (header === 'total_boxes') {
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-white/[0.06] text-gray-800 dark:text-gray-200 font-bold text-xs">
                        📦 {val}
                    </span>
                );
            }

            if (header === 'total_weight') {
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 font-bold text-xs">
                        {val} kg
                    </span>
                );
            }

            if (header === 'fm_pickup') {
                const isTrue = String(val).toUpperCase() === 'TRUE';
                return (
                    <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${
                        isTrue ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                        {isTrue ? 'Yes' : 'No'}
                    </span>
                );
            }

            if (header === 'insurance') {
                return (
                    <span className="inline-flex px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 text-xs font-medium">
                        {val || '—'}
                    </span>
                );
            }

            if (header === 'order_id' || header === 'lrn') {
                if (!val) {
                    return <span className="text-gray-400 dark:text-gray-500 italic text-[11px]">Auto</span>;
                }
                return <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">{val}</span>;
            }

            return (
                <span className="text-gray-800 dark:text-gray-200 text-xs font-medium">
                    {val !== null && val !== undefined && val !== '' ? String(val) : '—'}
                </span>
            );
        };

        return createPortal(
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-3 sm:p-5"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white dark:bg-[#0c0c17] rounded-2xl shadow-2xl dark:shadow-[0_20px_80px_rgba(79,70,229,0.4)] w-full max-w-[96vw] xl:max-w-7xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-200 dark:border-indigo-500/30"
                    >
                        {/* Header */}
                        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-white/15 backdrop-blur shadow-sm">
                                    <FileText className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base font-bold text-white leading-tight">Bulk Upload Preview</h2>
                                        <span className="px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-semibold text-white">
                                            {filteredData.length} records
                                        </span>
                                    </div>
                                    <p className="text-purple-100 text-xs leading-tight mt-0.5">
                                        Verify your CSV data before creating bulk orders
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2.5">
                                {/* Search input */}
                                <div className="relative">
                                    <Search className="w-3.5 h-3.5 text-white/60 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search records..."
                                        className="pl-8 pr-3 py-1.5 text-xs rounded-lg bg-white/15 border border-white/20 text-white placeholder-white/60 outline-none focus:bg-white/25 focus:border-white/40 transition-all w-36 sm:w-52"
                                    />
                                </div>

                                {/* View Mode Toggle */}
                                <div className="inline-flex rounded-lg bg-black/25 p-0.5 border border-white/15 text-xs font-semibold">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('formatted')}
                                        className={`px-2.5 py-1 rounded-md transition-all ${
                                            viewMode === 'formatted' ? 'bg-white text-indigo-700 shadow-sm' : 'text-white/80 hover:text-white'
                                        }`}
                                    >
                                        Formatted Table
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('raw')}
                                        className={`px-2.5 py-1 rounded-md transition-all ${
                                            viewMode === 'raw' ? 'bg-white text-indigo-700 shadow-sm' : 'text-white/80 hover:text-white'
                                        }`}
                                    >
                                        Raw CSV
                                    </button>
                                </div>

                                <button
                                    onClick={closePreviewModal}
                                    className="p-1.5 bg-white/15 hover:bg-white/25 active:scale-95 rounded-full transition-all text-white"
                                    title="Close"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Table Area */}
                        <div className="overflow-auto flex-1 p-3 sm:p-4 bg-slate-50/60 dark:bg-[#070711]">
                            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-indigo-500/25 bg-white dark:bg-[#0c0c18] shadow-sm">
                                <div className="overflow-x-auto max-h-[66vh]">
                                    <table className="w-full text-xs text-left border-collapse">
                                        <thead className="sticky top-0 z-10 bg-slate-100/95 dark:bg-[#121226]/95 backdrop-blur border-b border-gray-200 dark:border-indigo-500/30">
                                            <tr>
                                                <th className="px-3 py-3 text-center font-bold text-gray-500 dark:text-indigo-300 text-[11px] uppercase tracking-wider w-12 border-r border-gray-200 dark:border-indigo-500/20">
                                                    #
                                                </th>
                                                {headers.map((header, index) => (
                                                    <th
                                                        key={index}
                                                        className="px-3.5 py-3 font-bold text-indigo-800 dark:text-indigo-200 text-[11px] uppercase tracking-wider border-r border-gray-200 dark:border-indigo-500/20 last:border-r-0 whitespace-nowrap"
                                                    >
                                                        {getHeaderLabel(header)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-indigo-500/15">
                                            {filteredData.length === 0 ? (
                                                <tr>
                                                    <td colSpan={headers.length + 1} className="py-12 text-center text-gray-400 dark:text-indigo-300/60 text-xs">
                                                        No matching records found for "{searchTerm}"
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredData.map((row, rowIndex) => (
                                                    <tr
                                                        key={rowIndex}
                                                        className={`${
                                                            rowIndex % 2 === 0 ? 'bg-white dark:bg-[#0b0b14]' : 'bg-slate-50/70 dark:bg-[#0f0f1e]'
                                                        } hover:bg-indigo-50/70 dark:hover:bg-indigo-500/15 transition-colors`}
                                                    >
                                                        <td className="px-3 py-3 text-center font-bold text-gray-400 dark:text-indigo-300/60 border-r border-gray-100 dark:border-indigo-500/15">
                                                            {rowIndex + 1}
                                                        </td>
                                                        {headers.map((header, colIndex) => (
                                                            <td
                                                                key={colIndex}
                                                                className="px-3.5 py-3 border-r border-gray-100 dark:border-indigo-500/15 last:border-r-0 align-top"
                                                            >
                                                                {renderCell(header, row[header])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-[#0c0c17] border-t border-gray-100 dark:border-indigo-500/20">
                            <span className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-indigo-200/80">
                                <CheckCircle className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                                <span>
                                    Showing <strong className="text-gray-900 dark:text-white">{filteredData.length}</strong> of <strong className="text-gray-900 dark:text-white">{previewData.length}</strong> rows • {headers.length} columns loaded
                                </span>
                            </span>
                            <button
                                onClick={closePreviewModal}
                                className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-95 text-white rounded-xl font-bold text-xs shadow-md shadow-indigo-500/25 transition-all"
                            >
                                Close Preview
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>,
            document.body
        );
    };

    if (!isopen) return null;

    return (
        <Dialog
            open={isopen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: isDarkMode
                            ? 'rgba(0, 0, 0, 0.8)'
                            : 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                    }
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: '26px',
                    backgroundColor: isDarkMode ? '#0c0c18' : '#ffffff',
                    backgroundImage: isDarkMode
                        ? 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 60%), linear-gradient(135deg, #0b0b14 0%, #0d0d18 50%, #0b0b14 100%)'
                        : 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 60%), none',
                    boxShadow: isDarkMode
                        ? '0 30px 100px -15px rgba(0, 0, 0, 0.95), 0 0 0 1px rgba(99, 102, 241, 0.35), 0 0 50px rgba(79, 70, 229, 0.25)'
                        : '0 25px 70px -10px rgba(79, 70, 229, 0.25), 0 0 0 1px rgba(99, 102, 241, 0.15)',
                    overflow: 'hidden',
                    border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.35)' : 'none',
                    maxWidth: '520px',
                    width: '100%',
                }
            }}
        >
            <DialogTitle
                sx={{
                    p: 0,
                    m: 0,
                    position: 'relative',
                }}
            >
                <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white px-6 py-5">
                    {/* Ambient subtle glow lights */}
                    <div className="absolute -top-10 -right-10 w-36 h-36 bg-purple-400/25 rounded-full blur-2xl pointer-events-none" />
                    <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-indigo-400/25 rounded-full blur-2xl pointer-events-none" />

                    <div className="flex items-center justify-between gap-3 relative z-20">
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/25 flex items-center justify-center shrink-0">
                                <FiUploadCloud className="w-6 h-6 text-white" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-lg font-bold text-white tracking-tight leading-tight">Bulk Order Upload</h2>
                                    <span className="px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-bold text-white tracking-wide uppercase">
                                        B2B Manifest
                                    </span>
                                </div>
                                <p className="text-purple-100 text-xs leading-snug mt-0.5">
                                    Import CSV to generate multiple shipments at once
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCloseBulk();
                            }}
                            className="relative z-30 shrink-0 w-8 h-8 bg-white/15 hover:bg-white/30 active:scale-90 rounded-full flex items-center justify-center transition-all text-white backdrop-blur-sm border border-white/25 shadow-sm cursor-pointer"
                            title="Close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4 pointer-events-none" />
                        </button>
                    </div>
                </div>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: isDarkMode ? '#0b0b14' : '#ffffff',
                    padding: '24px !important',
                    '&::-webkit-scrollbar': { width: '5px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? 'rgba(99, 102, 241, 0.5)' : '#c4b5fd',
                        borderRadius: '3px'
                    },
                }}
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Upload Drop Zone */}
                    {!file ? (
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('dropzone-file')?.click()}
                            className={`group relative flex flex-col items-center justify-center text-center p-7 sm:p-8 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-200 ${
                                isDragging
                                    ? 'border-indigo-500 bg-indigo-500/15 scale-[1.01] shadow-lg shadow-indigo-500/20'
                                    : isDarkMode
                                        ? 'border-indigo-400/40 bg-indigo-500/[0.04] hover:bg-indigo-500/[0.08] hover:border-indigo-400/80'
                                        : 'border-purple-200 bg-gradient-to-b from-purple-50/50 to-indigo-50/30 hover:border-indigo-400 hover:bg-purple-50/80 shadow-sm'
                            }`}
                        >
                            <input
                                id="dropzone-file"
                                type="file"
                                className="hidden"
                                accept=".csv"
                                onChange={handleFileChange}
                            />

                            <div className="relative mb-3">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/30 group-hover:scale-110 group-hover:shadow-indigo-500/50 transition-all duration-300">
                                    <FiUploadCloud className="w-7 h-7" />
                                </div>
                                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white dark:border-[#0b0b14] animate-ping" />
                                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white dark:border-[#0b0b14]" />
                            </div>

                            <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Drop CSV here, or <span className="text-indigo-600 dark:text-indigo-400 underline underline-offset-2">browse files</span>
                            </p>
                            <p className="text-xs text-gray-400 dark:text-indigo-200/60 mt-1 max-w-xs">
                                Quickly import multiple receiver orders with dimensions
                            </p>

                            <div className="mt-3.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200/60 dark:border-indigo-500/30 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
                                <span>📄 .CSV format only • Max 10MB</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Selected File Card */}
                            <div className="p-4 rounded-2xl border border-emerald-300 dark:border-emerald-500/40 bg-gradient-to-r from-emerald-50/80 via-teal-50/40 to-emerald-50/80 dark:from-emerald-950/20 dark:via-teal-950/10 dark:to-emerald-950/20 shadow-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/30 shrink-0">
                                            <CheckCircle className="w-6 h-6" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">
                                                {file.name}
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300 font-medium mt-0.5">
                                                <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                                <span>•</span>
                                                <span>{previewData.length} records ready</span>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleClearFile}
                                        className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shrink-0"
                                        title="Remove file"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Preview & Change Actions */}
                                <div className="mt-3 pt-3 border-t border-emerald-200/60 dark:border-emerald-500/20 flex gap-2">
                                    {csvParsed && previewData.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handlePreviewClick}
                                            className="flex-1 flex items-center justify-center gap-2 py-2 px-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/25 active:scale-95 transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                            Preview {previewData.length} Rows in Table
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('dropzone-file')?.click()}
                                        className="px-3.5 py-2 rounded-xl border border-gray-200 dark:border-indigo-500/30 text-gray-700 dark:text-gray-200 text-xs font-semibold hover:bg-gray-100 dark:hover:bg-indigo-500/15 transition-all"
                                    >
                                        Change File
                                    </button>
                                    <input
                                        id="dropzone-file"
                                        type="file"
                                        className="hidden"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={!file || loading}
                        className={`w-full py-3.5 px-5 font-bold text-sm rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
                            !file || loading
                                ? isDarkMode
                                    ? 'bg-indigo-500/10 text-indigo-300/40 border border-indigo-500/25 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white shadow-lg shadow-indigo-500/30 active:scale-[0.98]'
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <Spinner /> Creating Orders...
                            </span>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                <span>
                                    {file && previewData.length > 0
                                        ? `Create ${previewData.length} Bulk Orders Now`
                                        : 'Create Bulk Orders'}
                                </span>
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </>
                        )}
                    </button>
                </form>

                {/* Sample Template Section */}
                <div className="mt-4 flex items-center justify-between p-3.5 bg-gradient-to-r from-purple-50/70 to-indigo-50/70 dark:from-indigo-950/20 dark:to-purple-950/20 dark:border dark:border-indigo-500/25 rounded-2xl border border-purple-100/80 shadow-sm">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-800 dark:text-gray-100 leading-tight">Sample Format</p>
                            <p className="text-[11px] text-gray-500 dark:text-indigo-300/70 leading-tight mt-0.5">Includes exact 17 B2B columns</p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleDownloadSample}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-indigo-500/20 hover:bg-indigo-50 dark:hover:bg-indigo-500/30 text-indigo-600 dark:text-indigo-300 rounded-xl text-xs font-bold border border-indigo-200 dark:border-indigo-500/30 shadow-sm transition-all active:scale-95 cursor-pointer"
                    >
                        <FiDownload className="w-3.5 h-3.5" />
                        <span>Download CSV</span>
                    </button>
                </div>

                {/* Modals */}
                <B2BConfirmBulkOrder
                    isopen={isPopupOpen}
                    onClose={handleClose}
                    data={reqBody}
                    file={file}
                />
                {showPreview && <PreviewModal />}
            </DialogContent>
        </Dialog>
    );
};

export default B2BUploadBulk;